import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";

export default function HelpChat() {
  const { user } = useAuth();
  const { t, lang } = useLocale();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const bottomRef = useRef(null);

  const sessionKey = user ? `luxe_help_session_${user.user_id}` : null;

  const getSessionId = useCallback(() => {
    let sid = localStorage.getItem(sessionKey);
    if (!sid) {
      sid = (crypto.randomUUID ? crypto.randomUUID() : `hs_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(sessionKey, sid);
    }
    return sid;
  }, [sessionKey]);

  useEffect(() => {
    if (open && !historyLoaded && user) {
      api.get(`/help/chat/${getSessionId()}`)
        .then((res) => {
          setMessages(res.data.messages.map((m) => ({ role: m.role, content: m.content })));
          setHistoryLoaded(true);
        })
        .catch(() => setHistoryLoaded(true));
    }
  }, [open, historyLoaded, user, getSessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, open]);

  if (!user) return null;

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await api.post("/help/chat", { session_id: getSessionId(), message: text, lang });
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.reply }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: t("help.error") },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-40 w-13 h-13 p-3.5 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:scale-105 transition-transform"
          data-testid="help-chat-button"
          aria-label="Open help assistant"
        >
          <Sparkles className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-50 w-[calc(100vw-2rem)] max-w-sm h-[480px] max-h-[70vh] bg-[#1a1226] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="help-chat-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-primary/10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold leading-none">{t("help.title")}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{t("help.subtitle")}</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-full hover:bg-white/10 text-muted-foreground"
              data-testid="help-chat-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="help-chat-messages">
            {messages.length === 0 && !loading && (
              <div className="text-center text-muted-foreground text-sm py-8">
                <Sparkles className="w-8 h-8 mx-auto mb-3 text-primary/60" />
                <p className="font-medium text-white/90 mb-1">{t("help.hi", { name: user.name ? ` ${user.name.split(" ")[0]}` : "" })}</p>
                <p className="text-xs leading-relaxed px-4">
                  {t("help.welcome")}
                </p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-md"
                      : "bg-white/5 border border-white/10 rounded-bl-md"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/10 px-3.5 py-2.5 rounded-2xl rounded-bl-md flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("help.thinking")}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="p-3 border-t border-white/10 flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder={t("help.inputPh")}
              className="flex-1"
              data-testid="help-chat-input"
            />
            <Button
              size="icon"
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              data-testid="help-chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
